'use client';
import * as React from 'react';
import { Icon } from './icon';
import { HintHover } from '@/components/ui/hint-hover';
import { cn } from '@/lib/utils';

const SEAL = {
  sm: { btn: 'h-6 w-6', icon: 13, offset: '-bottom-0.5 -right-0.5' },
  md: { btn: 'h-8 w-8', icon: 16, offset: '-bottom-1 -right-1' },
  lg: { btn: 'h-9 w-9', icon: 18, offset: '-bottom-1 -right-1' },
} as const;

/**
 * A round photo's bottom-right box corner is OUTSIDE the circle, and the gap grows
 * with the diameter (a 184px avatar leaves the seal floating ~27px off the edge).
 * So a circular frame anchors the seal on the arc at 45 degrees instead: inset the
 * box edge by (1 - cos45)/2 = 14.64% of the frame, then push back by half the seal
 * with the translate. Independent of both the photo size and the seal size.
 */
const CIRCLE_OFFSET = 'bottom-[14.64%] right-[14.64%] translate-x-1/2 translate-y-1/2';

/** Stage seal (public page before approval, owner decision 2026-10-05): one icon and one
 *  tint per tone. `gold` is the stored id of the approved seal, drawn in the brand green; yellow and red reuse
 *  the warning/destructive tokens so no new colour enters the palette. */
const TONE = {
  gold: { icon: 'checkmark-badge-01', className: 'bg-medlux text-medlux-foreground' },
  yellow: { icon: 'clock-01', className: 'bg-warning text-warning-foreground' },
  red: { icon: 'alert-02', className: 'bg-destructive text-white' },
} as const;

export interface PhotoSeal {
  tone: keyof typeof TONE;
  label: string;
  description?: string;
}

/** Seal trigger must forward ref for HintHover / Radix asChild. */
const SealButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { iconSize: number; icon?: string }
>(function SealButton({ className, iconSize, icon, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn('pointer-events-auto', className)}
      {...props}
    >
      <Icon name={icon ?? 'checkmark-badge-01'} size={iconSize} className="relative z-[1]" />
    </button>
  );
});

/**
 * Photo / avatar frame with an optional verified seal. The seal sits on the
 * image, and HintHover reveals the translated "Verificado" label on hover/focus
 * (no Tooltip primitive — house rule).
 */
export function VerifiedPhoto({
  verified,
  seal: sealProp,
  label,
  size = 'md',
  shape = 'rect',
  className,
  sealClassName,
  children,
}: {
  /** Legacy binary mark: renders the approved (green) seal with `label`. Prefer `seal`. */
  verified?: boolean;
  /** Stage seal with its own copy (from `useApprovalSeal`); wins over `verified`. */
  seal?: PhotoSeal | null;
  /** Translated hover + accessible name (e.g. publicBooking.verified). */
  label: string;
  size?: keyof typeof SEAL;
  /** Frame outline the seal has to hug: rounded card corner vs a round avatar. */
  shape?: 'rect' | 'circle';
  className?: string;
  sealClassName?: string;
  children: React.ReactNode;
}) {
  const seal = SEAL[size];
  const stage: PhotoSeal | null = sealProp ?? (verified ? { tone: 'gold', label } : null);
  const tone = stage ? TONE[stage.tone] : null;
  const hint = stage?.description ? `${stage.label}. ${stage.description}` : (stage?.label ?? label);
  return (
    // w-fit is load-bearing: the seal is positioned against THIS frame, so it must hug the
    // photo. As a flex-column item the span otherwise stretches to the container's width
    // (align-items: stretch) and the seal floats at 14.64% of the whole row — the public
    // profile's mobile cover shipped exactly that. Callers with an explicit width still
    // win (className merges last).
    <span className={cn('relative inline-flex w-fit shrink-0', className)}>
      {children}
      {stage && tone ? (
        <HintHover
          label={hint}
          side="top"
          align="center"
          contentClassName="border-white/20 bg-medlux-strong text-white shadow-card [&_p]:text-[0.8125rem] [&_p]:font-medium [&_p]:tracking-wide [&_p]:text-white"
        >
          <SealButton
            aria-label={hint}
            iconSize={seal.icon}
            icon={tone.icon}
            className={cn(
              'absolute z-10 inline-flex items-center justify-center rounded-full',
              'ring-2 ring-white',
              tone.className,
              'transition-transform duration-200 hover:scale-110 focus-visible:scale-110',
              // Focus ring lives in unlayered .medlux-verified-seal:focus-visible —
              // Tailwind ring utilities lose to that cascade.
              'medlux-verified-seal',
              seal.btn,
              shape === 'circle' ? CIRCLE_OFFSET : seal.offset,
              sealClassName,
            )}
          />
        </HintHover>
      ) : null}
    </span>
  );
}
